import {
  isDefinedArray,
  isDefinedString,
  isNullOrUndefined,
  isNumber
} from "et3-type-guards";
import { memoize, orderBy } from "lodash";
import { computed, observable, runInAction } from "mobx";
import queryString from "query-string";

import { apiCallGetMyCSRMistakes } from "../../../api/csr-api/api-call-get-my-csr-mistakes";
import { getSpecificDateWithDays } from "../../../core/et3-date";
import { TAuditEvent } from "../../../Types";
import getCorePersonalStore from "../../core/core-personal-store";
import getCorePopUpStore from "../../core/core-popup-store";
import getCoreUIStore from "../../core/core-ui-store";
import { TEt3PickerSingleValue } from "../../../TypesNEW";

class MyCSRMistakesStore {
  fromDate = observable.box<Date>(
    getSpecificDateWithDays({
      daysSubtracted: 1,
      daysAdded: 0
    })
  );
  toDate = observable.box<Date>(
    getSpecificDateWithDays({ daysSubtracted: 0, daysAdded: 1 })
  );
  myMistakes = observable.box<TAuditEvent[]>([]);
  mistakeType = observable.box<TEt3PickerSingleValue>({
    label: "All",
    value: "all"
  });
  issueSeverity = observable.box<TEt3PickerSingleValue>({
    label: "All",
    value: "all"
  });

  checkURLSearch = async (search: string) => {
    if (isDefinedString(search)) {
      const eventName = queryString.parse(search).eventName;
      const issueType = queryString.parse(search).issueType;
      const dateMin = `${queryString.parse(search).dateMin}`;
      const dateMax = `${queryString.parse(search).dateMax}`;
      if (isDefinedString(eventName) && isDefinedString(issueType)) {
        getMyCSRMistakesStore().setMistakeTypeAndSeverity(eventName, issueType);
        getMyCSRMistakesStore().fromDate.set(new Date(dateMin));
        getMyCSRMistakesStore().toDate.set(new Date(dateMax));
        await getMyCSRMistakesStore().getMistakes();
      }
    }
  };

  setIssueSeverity = (type: TEt3PickerSingleValue) => {
    runInAction(() => {
      this.issueSeverity.set(type);
    });
  };
  setMistakeType = (type: TEt3PickerSingleValue) => {
    runInAction(() => {
      this.mistakeType.set(type);
    });
  };

  getMistakes = async () => {
    const adminId = getCorePersonalStore().user.get().id;
    const definedNumber = isNumber(adminId) ? adminId : parseInt(adminId, 10);
    const type = this.mistakeType.get().value;
    const issueSeverity = this.issueSeverity.get().value;
    if (!isNumber(definedNumber) || definedNumber === -1) {
      return;
    }

    getCoreUIStore().setIsLoading("CSR_MY_MISTAKES", true);
    const { data, status, code } = await apiCallGetMyCSRMistakes({
      adminId: adminId,
      dateMin: this.fromDate.get(),
      dateMax: this.toDate.get(),
      eventType: type,
      issueType: issueSeverity
    });
    getCoreUIStore().setIsLoading("CSR_MY_MISTAKES", false);
    if (status === "OK") {
      if (!isNullOrUndefined(data) && isDefinedArray(data)) {
        this.myMistakes.set(data);
      } else {
        this.myMistakes.set([]);
        getCorePopUpStore().showAlertMessage("EVENTS_NOT_FOUND");
      }
    } else {
      this.myMistakes.set([]);
      getCorePopUpStore().showAlertMessage(code);
    }
  };

  myMistakesOrdered = computed(() => {
    const orderedMistakes: TAuditEvent[] = orderBy(
      this.myMistakes.get(),
      ["audited_on"],
      ["desc"]
    );
    return orderedMistakes;
  });

  setMistakeTypeAndSeverity = (eventName: string, issueSeverity: string) => {
    switch (eventName) {
      case "approval_description":
        this.mistakeType.set({
          value: "approval_description",
          label: "Approve/Reject Description"
        });
        break;
      case "approval_peer_description":
        this.mistakeType.set({
          value: "approval_peer_description",
          label: "Approve/Reject Peer Description"
        });
        break;
      case "approval_picture":
        this.mistakeType.set({
          value: "approval_picture",
          label: "Approve/Reject Pictures"
        });
        break;
      case "report":
        this.mistakeType.set({
          value: "report",
          label: "Report"
        });
        break;
      case "investigation_cases":
        this.mistakeType.set({
          value: "investigation_cases",
          label: "Investigation Cases"
        });
        break;
      case "all":
        this.mistakeType.set({
          value: "all",
          label: "All"
        });
        break;
    }
    switch (issueSeverity) {
      case "no_issue":
        this.issueSeverity.set({
          value: "no_issue",
          label: "No Issue"
        });
        break;
      case "minor":
        this.issueSeverity.set({
          value: "minor",
          label: "Minor"
        });
        break;
      case "major":
        this.issueSeverity.set({
          value: "major",
          label: "Major"
        });
        break;
      case "all":
        this.issueSeverity.set({
          value: "all",
          label: "All"
        });
    }
  };
}

const getMyCSRMistakesStore = memoize(
  () => new MyCSRMistakesStore(),
  () => 1
);
export default getMyCSRMistakesStore;
